import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import 'leaflet/dist/leaflet.css';
import 'react-day-picker/dist/style.css';

// ── Build version stamp ───────────────────────────────────────────
// Bumped manually on every deploy that needs to invalidate the
// browser bundle cache. The string is referenced (via console.info)
// so webpack content-hashing always sees a different main bundle
// payload and emits a new filename hash on rebuild — which forces
// every browser / CDN edge to fetch the fresh JS instead of serving
// the previously-cached stale bundle.
//
// 3/6/2026 → bumped to 2026.06.03-telcavoip-cloud-backup
//   Added "TelcaVoIP Cloud" managed-backup option (pCloud-backed)
//   to the Admin Backup panel. LoJack admins see ONLY a toggle +
//   last-upload status; pCloud access token managed server-side
//   (PCLOUD_ACCESS_TOKEN / PCLOUD_API_REGION env vars). Hooked into
//   the existing nightly auto-backup scheduler so when enabled,
//   nightly snapshots are pushed to /LoJack-SecureWallet/backups/
//   {YYYY}/{MM}/lojack_backup_{TS}.json. Retention: forever (per
//   Fabrizio req. 03/06/2026 — option C / no auto-cleanup).
const BUILD_VERSION = "2026.06.03-telcavoip-cloud-backup";
if (typeof window !== "undefined") {
  // eslint-disable-next-line no-console
  console.info("[LoJack] build:", BUILD_VERSION);
  window.__LJ_BUILD__ = BUILD_VERSION;
}

import { AuthProvider, ProtectedRoute, useAuth } from "./context/AuthContext";
import { Navigation } from "./components/shared/Navigation";

import LoginPage from "./pages/LoginPage";
import CustomerWalletPage from "./pages/CustomerWalletPage";
import CustomerSelfUpdatePage from "./pages/CustomerSelfUpdatePage";
import DealerPortal from "./pages/DealerPortal";
import AdminDashboard from "./pages/AdminDashboard";
import AdminSystemHistoryPage from "./pages/AdminSystemHistoryPage";
import AdminFixCenterPage from "./pages/AdminFixCenterPage";
import CustomersList from "./pages/CustomersList";
import OperationCentersPage from "./pages/OperationCentersPage";
import SignedContractsPage from "./pages/SignedContractsPage";
import DocumentsPage from "./pages/DocumentsPage";
import DealersPage from "./pages/DealersPage";
import DealerPerformancePage from "./pages/DealerPerformancePage";
import AdminUsersPage from "./pages/AdminUsersPage";
import LegalDocumentsPage from "./pages/LegalDocumentsPage";
import PrivateDocsPage from "./pages/PrivateDocsPage";
import CountryDivergenceAuditPage from "./pages/CountryDivergenceAuditPage";
import TechnicalInfoPage from "./components/TechnicalInfoPage";
import TechnicalInfoAdmin from "./pages/TechnicalInfoAdmin";
import ManualsPage from "./pages/ManualsPage";
import ManualViewerPage from "./pages/ManualViewerPage";
import AdminManualSvgsStatusPage from "./pages/AdminManualSvgsStatusPage";
import PublicManualsPage from "./pages/PublicManualsPage";
import PublicManualViewerPage from "./pages/PublicManualViewerPage";
import OperationalManualPage from "./pages/OperationalManualPage";
import SecurityCompliancePage from "./pages/SecurityCompliancePage";
import SetupPasswordPage from "./pages/SetupPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DealerForcedChangePasswordPage from "./pages/DealerForcedChangePasswordPage";
import EmailPreviewPage from "./pages/EmailPreviewPage";
import EmailTemplateOverviewPage from "./pages/EmailTemplateOverviewPage";
import LegalContractPreviewPage from "./pages/LegalContractPreviewPage";
import AdminResetPasswordPage from "./pages/AdminResetPasswordPage";
import I18nStatusPage from "./pages/I18nStatusPage";
import AdminCommunicationsPage from "./pages/AdminCommunicationsPage";
import VinDecoderProPage from "./pages/VinDecoderProPage";
import SmsCreditPage from "./pages/SmsCreditPage";
import OperationalManualsAdminPage from "./pages/OperationalManualsAdminPage";
import { VinDecoderGate } from "./components/admin/VinDecoderGate";
import BuildVersionWatcher from "./components/shared/BuildVersionWatcher";
import ITPortalPage from "./pages/ITPortalPage";
import PublicStatusPage from "./pages/PublicStatusPage";
import ErasureRequestPage from "./pages/ErasureRequestPage";
import ErasureConfirmPage from "./pages/ErasureConfirmPage";
import SalesforcePage from "./pages/SalesforcePage";
import SalesforcePortalPage from "./pages/SalesforcePortalPage";
import AdminAuditDashboard from "./pages/AdminAuditDashboard";
import AdminDisasterRecovery from "./pages/AdminDisasterRecovery";
import AdminCustomerOverridesAudit from "./pages/AdminCustomerOverridesAudit";
import AdminStyleReferencePage from "./pages/AdminStyleReferencePage";

function AppContent() {
  const { user } = useAuth();

  return (
    <>
      <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: '#1A1A1A',
                color: '#fff',
                border: '1px solid #2A2A2A',
              },
            }}
          />
          <BuildVersionWatcher />
          <Routes>
            {/* Public Routes */}
            {/* Root path redirects to /login — Zorzutti CR 28/07/2026:
                the previous marketing landing page (HomePage) has been
                retired because securewallet.lojack.eu serves an
                authenticated B2B audience (dealers + admins) that
                does not need a promotional overview before authentication. */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/status" element={<PublicStatusPage />} />
            <Route path="/erasure" element={<ErasureRequestPage />} />
            <Route path="/erasure/confirm" element={<ErasureConfirmPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
            <Route path="/wallet/:customerId" element={<CustomerWalletPage />} />
            <Route path="/wallet/:customerId/edit" element={<CustomerSelfUpdatePage />} />
            <Route path="/dealer" element={<DealerPortal />} />
            <Route path="/dealer/manual" element={<OperationalManualPage role="dealer" backLink="/dealer" />} />
            <Route path="/seller/manual" element={<OperationalManualPage role="seller" backLink="/dealer" />} />
            <Route path="/dealers" element={<Navigate to="/dealer" replace />} />
            <Route path="/dealer/setup-password" element={<SetupPasswordPage />} />
            <Route path="/dealer/change-password" element={<DealerForcedChangePasswordPage />} />
            <Route path="/dealer/reset-password" element={<ResetPasswordPage />} />
            <Route path="/dealer/forgot-password" element={<ResetPasswordPage />} />
            
            {/* Public Manual Routes */}
            <Route path="/manuali" element={<PublicManualsPage />} />
            <Route path="/manuali/:manualId" element={<PublicManualViewerPage />} />
            
            {/* Protected Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute>
                <Navigation />
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/customers" element={
              <ProtectedRoute>
                <Navigation />
                <CustomersList />
              </ProtectedRoute>
            } />
            <Route path="/centers" element={
              <ProtectedRoute>
                <Navigation />
                <OperationCentersPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/contracts" element={
              <ProtectedRoute>
                <Navigation />
                <SignedContractsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/docs" element={
              <ProtectedRoute>
                <Navigation />
                <DocumentsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/dealers" element={
              <ProtectedRoute>
                <Navigation />
                <DealersPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/dealer-performance" element={
              <ProtectedRoute>
                <Navigation />
                <DealerPerformancePage />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute>
                <Navigation />
                <AdminUsersPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/email-preview" element={
              <ProtectedRoute>
                <Navigation />
                <EmailPreviewPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/email-templates/overview" element={
              <ProtectedRoute>
                <Navigation />
                <EmailTemplateOverviewPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/i18n-status" element={
              <ProtectedRoute>
                <Navigation />
                <I18nStatusPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/communications" element={
              <ProtectedRoute>
                <Navigation />
                <AdminCommunicationsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/operational-manuals" element={
              <ProtectedRoute>
                <Navigation />
                <OperationalManualsAdminPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/sms-credit" element={
              <ProtectedRoute>
                <Navigation />
                <SmsCreditPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/vin-decoder" element={
              <ProtectedRoute>
                <Navigation />
                <VinDecoderGate><VinDecoderProPage /></VinDecoderGate>
              </ProtectedRoute>
            } />
            <Route path="/admin/private-docs" element={
              <ProtectedRoute>
                <Navigation />
                <PrivateDocsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/legal" element={
              <ProtectedRoute>
                <Navigation />
                <LegalDocumentsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/legal-preview" element={
              <ProtectedRoute>
                <Navigation />
                <LegalContractPreviewPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/audits/country-divergence" element={
              <ProtectedRoute>
                <Navigation />
                <CountryDivergenceAuditPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/technical-info" element={
              <ProtectedRoute>
                <Navigation />
                <TechnicalInfoAdmin />
              </ProtectedRoute>
            } />
            <Route path="/admin/security" element={
              <ProtectedRoute>
                <Navigation />
                <SecurityCompliancePage />
              </ProtectedRoute>
            } />
            <Route path="/admin/salesforce" element={
              <ProtectedRoute>
                <Navigation />
                <SalesforcePage />
              </ProtectedRoute>
            } />
            <Route path="/admin/audit-dashboard" element={
              <ProtectedRoute>
                <Navigation />
                <AdminAuditDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/disaster-recovery" element={
              <ProtectedRoute>
                <Navigation />
                <AdminDisasterRecovery />
              </ProtectedRoute>
            } />
            <Route path="/admin/audit/customer-overrides" element={
              <ProtectedRoute>
                <Navigation />
                <AdminCustomerOverridesAudit />
              </ProtectedRoute>
            } />
            <Route path="/admin/system/history" element={
              <ProtectedRoute>
                <Navigation />
                <AdminSystemHistoryPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/system-health" element={
              <ProtectedRoute>
                <Navigation />
                <AdminFixCenterPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/style-reference" element={
              <ProtectedRoute>
                <Navigation />
                <AdminStyleReferencePage />
              </ProtectedRoute>
            } />
            <Route path="/admin/manuals" element={
              <ProtectedRoute>
                <Navigation />
                <ManualsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/manuals/svgs-status" element={
              <ProtectedRoute>
                <Navigation />
                <AdminManualSvgsStatusPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/manuals/:manualId" element={
              <ProtectedRoute>
                <Navigation />
                <ManualViewerPage />
              </ProtectedRoute>
            } />
            
            {/* Salesforce Portal - standalone access */}
            <Route path="/salesforce" element={<SalesforcePortalPage />} />

            {/* IT Portal - standalone access */}
            <Route path="/it-portal" element={<ITPortalPage />} />

            <Route path="/technical-info/ATU1650MB" element={<TechnicalInfoPage />} />
            <Route path="/technical-info" element={<TechnicalInfoPage />} />
            
            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
